import PhoneNumberCodeInfo from "@/components/core/labels/PhoneNumberCodeInfo";
import { transactionSettingsStore } from "@/state/transactions/TransactionSettingsStore";
import { ActivateSettings } from "@/type/core/transaction/transaction.types";
import BasicButton from "@components/core/buttons/BasicButton";
import InputFormField from "@components/core/inputs/InputFormField";
import MailFormField from "@components/core/inputs/MailFormField";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import InfoModal from "@components/core/modals/InfoModal";
import { faEnvelope, faPhone, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setCertificateInfosPhone } from "@state/auth/AuthEvents";
import { requestPostCertificateUrl } from "@state/onboarding/OnboardingEffects";
import CheckIcon from "@static/core/icons/icon-check.svg?react";
import CrossIcon from "@static/core/icons/icon-cross.svg?react";
import { ActiveUser } from "@type/core/auth/auth.types";
import { formValidateTriggers, ONBOARDING_ERROR_ENUM } from "@utils/Constant";
import { StatusEnum } from "@utils/enums/status.enum";
import { toastError } from "@utils/helpers/toast-helper";
import { Col, Form, Row } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useUnit } from "effector-react";
import parse from "html-react-parser";
import {
  Dispatch,
  FunctionComponent,
  ReactElement,
  SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";

interface OBHomePageProps {
  user: ActiveUser | undefined;
  setLoading: (loading: boolean) => void;
  isOnboardingPhoneErrorModalOpen: boolean;
  setIsOnboardingPhoneErrorModalOpen: Dispatch<SetStateAction<boolean>>;
  className?: string;
}

const OBHomePage: FunctionComponent<OBHomePageProps> = ({
  user,
  setLoading,
  isOnboardingPhoneErrorModalOpen,
  setIsOnboardingPhoneErrorModalOpen,
  className = "",
}) => {
  const { t } = useTranslation();

  const transactionSettings = useUnit<ActivateSettings | null>(
    transactionSettingsStore,
  );
  const onboardingIsBlocked = transactionSettings
    ? transactionSettings.onboardingIsBlocked
    : false;

  const [form] = useForm();
  const onboardingError = user?.onboardingError;
  const certificateStatus = user?.tsCertificateStatus;
  const inOrganization = user?.inOrganization;

  const InitialValues = {
    firstname: user?.firstname,
    lastname: user?.lastname,
    email: user?.email,
    phone: user?.certificateInfos?.phone,
  };

  const handleValidateOnboarding = (shouldCheckPhone: boolean): void => {
    setLoading(true);
    void requestPostCertificateUrl({
      dto: {
        phone: user?.certificateInfos?.phone || "",
        shouldCheckPhone: shouldCheckPhone,
      },
    })
      .then((res) => {
        if (res.ok) {
          if (res.data) {
            location.href = res.data?.url;
          }
        } else {
          if (res.data?.customCode === "INVALID_PHONE") {
            setIsOnboardingPhoneErrorModalOpen(true);
          } else {
            toastError(t("onboarding.home.getCertificateError"));
          }
        }
      })
      .finally(() => setLoading(false));
  };

  const getForm = (): ReactElement => {
    return (
      <Row gutter={30} className="form-onboarding justify-content-center my-48">
        <Col span={24} md={18} lg={12}>
          <Form
            form={form}
            initialValues={InitialValues}
            {...formValidateTriggers}
          >
            <Row gutter={30} className="align-items-center mb-16">
              <Col className="form-onboarding-label" span={20} md={8}>
                <label htmlFor="firstname">
                  {t("onboarding.home.label.firstname")}
                </label>
              </Col>
              <Col className="form-onboarding-icon" span={4} md={2}>
                <FontAwesomeIcon icon={faUser} />
              </Col>
              <Col className="form-onboarding-input" span={24} md={14}>
                <InputFormField
                  disabled
                  module="onboarding"
                  field="firstname"
                  placeholder=" "
                  className="mb-0"
                />
              </Col>
            </Row>
            <Row gutter={30} className="align-items-center mb-16">
              <Col className="form-onboarding-label" span={20} md={8}>
                <label htmlFor="lastname">
                  {t("onboarding.home.label.lastname")}
                </label>
              </Col>
              <Col className="form-onboarding-icon" span={4} md={2}>
                <FontAwesomeIcon icon={faUser} />
              </Col>
              <Col className="form-onboarding-input" span={24} md={14}>
                <InputFormField
                  disabled
                  module="onboarding"
                  field="lastname"
                  placeholder=" "
                  className="mb-0"
                />
              </Col>
            </Row>
            <Row gutter={30} className="align-items-center mb-16">
              <Col className="form-onboarding-label" span={20} md={8}>
                <label htmlFor="email">
                  {t("onboarding.home.label.email")}
                </label>
              </Col>
              <Col className="form-onboarding-icon" span={4} md={2}>
                <FontAwesomeIcon icon={faEnvelope} />
              </Col>
              <Col className="form-onboarding-input" span={24} md={14}>
                <MailFormField
                  disabled
                  module="onboarding"
                  field="email"
                  placeholder=" "
                  className="mb-0"
                />
              </Col>
            </Row>
            <Row gutter={30} className="align-items-center mb-16">
              <Col className="form-onboarding-label" span={20} md={10}>
                <label htmlFor="">{t("onboarding.home.label.offices")}</label>
              </Col>
              <Col className="form-onboarding-list" span={24} md={14}>
                <ul>
                  {user?.officesForCgu &&
                    user?.officesForCgu.map((office) => (
                      <li key={office.id} title={office.name}>
                        {office.cgu ? (
                          <CheckIcon className="text-success icon-30" />
                        ) : (
                          <CrossIcon className="text-danger icon-30" />
                        )}
                        <span>{office.name}</span>
                      </li>
                    ))}
                </ul>
              </Col>
            </Row>
            <Row gutter={30} className="align-items-center mb-16">
              <Col className="form-onboarding-label" span={20} md={8}>
                <label htmlFor="phone">
                  {t("onboarding.home.label.phone")}
                  <InfoModal value="phone" info />
                </label>
              </Col>
              <Col className="form-onboarding-icon" span={4} md={2}>
                <FontAwesomeIcon icon={faPhone} />
              </Col>
              <Col className="form-onboarding-input" span={24} md={14}>
                <InputFormField
                  module="onboarding"
                  field="phone"
                  placeholder=" "
                  className="mb-0"
                  onChange={(e) => setCertificateInfosPhone(e.target.value)}
                />
              </Col>
            </Row>
            <PhoneNumberCodeInfo />
          </Form>
          <ConfirmModal
            visible={isOnboardingPhoneErrorModalOpen}
            onCancel={() => setIsOnboardingPhoneErrorModalOpen(false)}
            onOk={() => {
              setIsOnboardingPhoneErrorModalOpen(false);
              handleValidateOnboarding(false);
            }}
            title={t("onboarding.home.onboardingPhoneError.title")}
            content={t("onboarding.home.onboardingPhoneError.body", {
              phone: user?.certificateInfos?.phone,
            })}
          />
        </Col>
      </Row>
    );
  };

  const getErrorType = (onboardingError?: string): string => {
    return onboardingError ? onboardingError : "default";
  };

  const getButton = (onboardingError?: string): ReactElement => {
    return (
      <div className="text-center">
        <BasicButton
          variant="tertiary"
          text={t(`onboarding.home.${getErrorType(onboardingError)}.validate`)}
          onClick={() => handleValidateOnboarding(true)}
          disabled={
            !user?.certificateInfos?.phone ||
            ((user?.certificateInfos?.phone &&
              user?.certificateInfos?.phone === "") as boolean) ||
            onboardingIsBlocked
          }
        />
        {onboardingIsBlocked && (
          <div className="text-danger mt-4">{t("onboarding.home.blocked")}</div>
        )}
      </div>
    );
  };

  const getText = (onboardingError?: string, n?: number): ReactElement => {
    return (
      <>
        {parse(t(`onboarding.home.${getErrorType(onboardingError)}.text${n}`))}
      </>
    );
  };

  const getMail = (): string => {
    return t("onboarding.home.mail");
  };

  const getContent = (onboardingError?: string): ReactElement => {
    switch (onboardingError) {
      case ONBOARDING_ERROR_ENUM.NEW_EMAIL:
        return (
          <div className={`app-onboarding onboarding-home ${className}`}>
            <p>{getText(onboardingError, 1)}</p>
            <p>{getText(onboardingError, 2)}</p>
            <p>
              {getText(onboardingError, 3)}
              <ul>
                <li>
                  {getText(onboardingError, 4)}
                  <a href={t("onboarding.home.revokationLink")}>
                    {t("onboarding.home.revokationLink")}
                  </a>
                </li>
                <li>
                  {getText(onboardingError, 5)}
                  <a href={`mailto:${getMail()}`}>{getMail()}</a>
                  {getText(onboardingError, 6)}
                </li>
              </ul>
            </p>
            <p>
              <u>{getText(onboardingError, 7)}</u>
              {getText(onboardingError, 8)}
            </p>
            <p>
              <b>{getText(onboardingError, 9)}</b>
            </p>
            {getForm()}
            <p>
              <i>
                {getText(onboardingError, 10)}
                <b>{getText(onboardingError, 11)}</b>
              </i>
            </p>
            {getButton(onboardingError)}
          </div>
        );
      case ONBOARDING_ERROR_ENUM.ACCOUNT_DELETED:
        return (
          <div className={`app-onboarding onboarding-home ${className}`}>
            <p>{getText(onboardingError, 1)}</p>
            <p>{getText(onboardingError, 2)}</p>
            <p>
              <b>{getText(onboardingError, 3)}</b>
            </p>
            {getForm()}
            <p>
              <i>
                {getText(onboardingError, 4)}
                <b>{getText(onboardingError, 5)}</b>
              </i>
            </p>
            {getButton(onboardingError)}
          </div>
        );
      case ONBOARDING_ERROR_ENUM.NOT_IN_ORGANIZATION:
        return (
          <div className={`app-onboarding onboarding-home ${className}`}>
            <p>{getText(onboardingError, 1)}</p>
            <p>
              {getText(onboardingError, 2)}
              <ul>
                <li>
                  {getText(onboardingError, 3)}
                  <a href={t("onboarding.home.revokationLink")}>
                    {t("onboarding.home.revokationLink")}
                  </a>
                </li>
                <li>
                  {getText(onboardingError, 4)}
                  <a href={`mailto:${getMail()}`}>{getMail()}</a>
                  {getText(onboardingError, 5)}
                </li>
              </ul>
            </p>
          </div>
        );
      case ONBOARDING_ERROR_ENUM.CERTIFICATE_INVALID:
        return (
          <div className={`app-onboarding onboarding-home ${className}`}>
            <p>{getText(onboardingError, 1)}</p>
            <p>
              {getText(onboardingError, 2)}
              <a href={`mailto:${getMail()}`}>{getMail()}</a>
              {getText(onboardingError, 3)}
            </p>
            <p>{getText(onboardingError, 4)}</p>
            <p>{getText(onboardingError, 5)}</p>
            <p>{getText(onboardingError, 6)}</p>
            <p>
              <b>{getText(onboardingError, 7)}</b>
            </p>
            <p>{getText(onboardingError, 8)}</p>
            <p>{getText(onboardingError, 9)}</p>
            {getForm()}
            <p>
              <i>
                {getText(onboardingError, 10)}
                <b>{getText(onboardingError, 11)}</b>
              </i>
            </p>
            {getButton(onboardingError)}
          </div>
        );
      default:
        return (
          <div className={`app-onboarding onboarding-home ${className}`}>
            <p>
              {parse(
                t(
                  `onboarding.home.${
                    certificateStatus === StatusEnum.STARTED
                      ? "STARTED"
                      : "default"
                  }.text1`,
                ),
              )}
            </p>
            <p>
              <strong>
                {parse(
                  t(
                    `onboarding.home.${
                      certificateStatus === StatusEnum.STARTED
                        ? "STARTED"
                        : "default"
                    }.text2`,
                  ),
                )}
              </strong>
            </p>
            <p>
              {parse(
                t(
                  `onboarding.home.${
                    certificateStatus === StatusEnum.STARTED
                      ? "STARTED"
                      : "default"
                  }.text3`,
                ),
              )}
            </p>
            {certificateStatus !== StatusEnum.STARTED && (
              <p>{parse(t("onboarding.home.default.text4"))}</p>
            )}
            {getForm()}
            <p>
              <i>
                {parse(t("onboarding.home.default.text5"))}
                <strong>{parse(t("onboarding.home.default.text6"))}</strong>
              </i>
            </p>
            {getButton()}
          </div>
        );
    }
  };

  return !certificateStatus ||
    certificateStatus === StatusEnum.INVALID ||
    certificateStatus === StatusEnum.FAILED ||
    certificateStatus === StatusEnum.STARTED ||
    certificateStatus === StatusEnum.NOT_STARTED ||
    !inOrganization ? (
    getContent(onboardingError)
  ) : (
    <></>
  );
};

export default OBHomePage;
